import React, { useCallback, useEffect, useState } from 'react';
import difference from 'lodash/difference';
import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy';
import { unstable_useBlocker as useBlocker } from 'react-router';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { removePostsAsync, updatePostStatusAsync } from '../../data-layer/posts/actions';
import { PostSource, PostStatus } from '../../data-layer/posts/constants';
import * as postsService from '../../data-layer/posts/service';
import { PostModel } from '../../data-layer/posts/types';
import { useDispatch } from '../../data-layer/useDispatch';
import { UserModel } from '../../data-layer/user/types';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { Alert, confirmAlert } from '../../ui/feedback/Alert';
import { WithHint } from '../../ui/feedback/Tooltip';
import { IconChecked } from '../../ui/icon/IconChecked';
import { IconChevron } from '../../ui/icon/IconChevron';
import { Button } from '../../ui/input/Button';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { InfiniteList } from '../../ui/layout/InfiniteList';
import { StickyFooter } from '../../ui/layout/StickyFooter';
import { Menu, MenuItem } from '../../ui/navigation/Menu';
import {
    FeedManagementHeader,
    FeedManagementRow,
    FeedManagementRowSkeleton,
} from '../../ui/posts/FeedManagementRow';
import { PostContextMenu } from '../../ui/posts/PostContextMenu';
import { getSourceDisplayText } from '../../ui/posts/getSourceDisplayText';
import { Typography } from '../../ui/typography/Typography';
import { hasAccessToFeedManagement } from '../../utils/accessService';
import { EventSource } from '../../utils/analyticsService';
import {
    ymTrackFeedManager,
    ymTrackFeedManagerBulkChangeStatus,
    ymTrackFeedManagerChangeFiltersParams,
    ymTrackFeedManagerSelectPost,
} from '../../utils/metrika';
import { useMeta } from '../../utils/useMeta';
import { PostCollectionFilters, usePostCollection } from '../post/usePostCollection';
import { withPostCallbacks } from '../post/withPostCallbacks';
import { useCurrentUser } from '../users/useCurrentUser';
import { withBusinessAuthProtection } from '../withBusinessAuthProtection';

const EnhancedPostContextMenu = withPostCallbacks(PostContextMenu, EventSource.BUSINESS);

const LIMIT = 20;

const QUERY_PARAMETER_STATUS = 'status';
const QUERY_PARAMETER_SOURCE = 'source';

const canChangeStatusBulk = (
    models: PostModel[],
    identifiers: string[],
    status: PostStatus,
    user: UserModel,
) =>
    identifiers.length > 0 &&
    models
        .filter((model) => identifiers.includes(model.id))
        .every((model) => postsService.canChangeStatus(model, status, user));

type Option<TValue> = {
    displayName: string;
    value: TValue;
};

const SOURCE_OPTIONS: Option<PostSource>[] = [
    { displayName: getSourceDisplayText(PostSource.MANUAL), value: PostSource.MANUAL },
    { displayName: getSourceDisplayText(PostSource.INSTAGRAM), value: PostSource.INSTAGRAM },
];

enum StatusFilter {
    ALL = 'all',
    MODERATION = 'moderation',
    DECLINED = 'declined',
    DRAFTS = 'drafts',
    WITHOUT_DRAFTS = 'without-drafts',
}

const STATUSES_OPTIONS: Option<StatusFilter>[] = [
    { displayName: 'Все посты', value: StatusFilter.ALL },
    { displayName: 'На модерации', value: StatusFilter.MODERATION },
    { displayName: 'Отклоненные', value: StatusFilter.DECLINED },
    { displayName: 'Только черновики', value: StatusFilter.DRAFTS },
    { displayName: 'Все, кроме черновиков', value: StatusFilter.WITHOUT_DRAFTS },
];

const STATUS_FILTER_MAPPING: Record<string, PostStatus[]> = {
    [StatusFilter.ALL]: [
        PostStatus.DRAFT,
        PostStatus.PUBLISHED,
        PostStatus.MODERATION,
        PostStatus.LIMITED,
        PostStatus.DECLINED,
    ],
    [StatusFilter.MODERATION]: [PostStatus.MODERATION],
    [StatusFilter.DECLINED]: [PostStatus.DECLINED],
    [StatusFilter.DRAFTS]: [PostStatus.DRAFT],
    [StatusFilter.WITHOUT_DRAFTS]: [
        PostStatus.PUBLISHED,
        PostStatus.MODERATION,
        PostStatus.LIMITED,
        PostStatus.DECLINED,
    ],
};

const makeFilters = (query: URLSearchParams): Partial<PostCollectionFilters> => {
    const source = query.get(QUERY_PARAMETER_SOURCE);
    const status = query.get(QUERY_PARAMETER_STATUS);

    return {
        data_sources: source ? [source] : [],
        statuses: STATUS_FILTER_MAPPING[status || StatusFilter.ALL],
    } as any;
};

interface DropdownProps<TValue> {
    placeholder: string;
    value?: TValue | null;
    options: Option<TValue>[];
    onChange: (value: TValue | null) => void;
}

export function Dropdown<TValue>(props: DropdownProps<TValue>) {
    const { placeholder, value, options, onChange } = props;
    const selectedOption = options.find((option) => option.value === value);

    return (
        <Menu
            trigger={
                <Button spacing={0.25} size="s">
                    <span>{selectedOption?.displayName ?? placeholder}</span>
                    <IconChevron size={20} />
                </Button>
            }
        >
            {options.map((option) => (
                <MenuItem
                    key={option.displayName}
                    spacing={1}
                    direction="row"
                    justifyContent="space-between"
                    onClick={() => onChange(value === option.value ? null : option.value)}
                >
                    <Typography variant="body" noWrap>
                        {option.displayName}
                    </Typography>
                    <IconChecked
                        size={20}
                        color={value === option.value ? 'var(--color-primary)' : 'transparent'}
                    />
                </MenuItem>
            ))}
        </Menu>
    );
}

const BORDER_RADIUS = { sm: 2 };

const BULK_SUCCESS_MESSAGE_MAPPING: Partial<Record<PostStatus, string>> = {
    [PostStatus.DELETED]: 'Выбранные посты удалены',
    [PostStatus.MODERATION]: 'Выбранные посты отправлены на модерацию',
};
const BULK_CONFIRM_MESSAGE_MAPPING: Partial<Record<PostStatus, string>> = {
    [PostStatus.DELETED]: 'Выбранные посты будут безвозвратно удалены',
    [PostStatus.MODERATION]: 'Выбранные посты будут отправлены на модерацию',
};

type IdentifierDictionary = Record<string, boolean>;

const makeIdentifiers = (mapping: IdentifierDictionary) => keys(pickBy(mapping, Boolean));

const isAllSelected = (models: PostModel[], identifiers: string[]) =>
    identifiers.length > 0 &&
    difference(
        models.map((model) => model.id),
        identifiers,
    ).length === 0;

const makeMapping = (models: PostModel[]): IdentifierDictionary =>
    models.reduce(
        (mapping, model) => ({
            ...mapping,
            [model.id]: true,
        }),
        {},
    );

export const FeedManagement = withBusinessAuthProtection(() => {
    const { businessId } = useParams();
    const [query, setQuery] = useSearchParams();
    const currentUser = useCurrentUser();
    const dispatch = useDispatch();
    const [selected, setSelected] = useState<IdentifierDictionary>({});
    const identifiers = makeIdentifiers(selected);
    const collection = usePostCollection({
        namespace: 'management',
        filters: {
            businessId,
            amount: LIMIT,
            ...makeFilters(query),
        },
    });

    const onBulkChangeStatus = useCallback(
        async (status: PostStatus) => {
            ymTrackFeedManagerBulkChangeStatus(status, {
                page: 'Управления постами',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            try {
                await confirmAlert('Подтверждение', BULK_CONFIRM_MESSAGE_MAPPING[status]!);
            } catch (error) {
                return;
            }

            const succeed = await dispatch(
                status === PostStatus.DELETED
                    ? removePostsAsync(...identifiers)
                    : updatePostStatusAsync(identifiers, status, currentUser),
            );

            if (succeed) {
                const message = BULK_SUCCESS_MESSAGE_MAPPING[status];

                if (message) {
                    dispatch(showNotificationAsync(message));
                }

                setSelected({});
            }
        },
        [
            dispatch,
            selected,
            setSelected,
            currentUser?.role,
            currentUser?.profile?.publicId,
            currentUser?.login,
        ],
    );

    const onSelectAll = useCallback(() => {
        if (isAllSelected(collection.items, identifiers)) {
            return setSelected({});
        }

        setSelected(makeMapping(collection.items));
    }, [selected, collection.items, setSelected]);

    const onSelect = useCallback(
        (postId: string, value: boolean) => {
            ymTrackFeedManagerSelectPost({
                page: 'Управления постами',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            setSelected({ ...selected, [postId]: value });
        },
        [selected, setSelected, currentUser?.login, currentUser?.profile?.publicId],
    );

    const unloadGuard = useBlocker(
        (context) =>
            context.nextLocation.pathname !== context.currentLocation.pathname &&
            Boolean(identifiers.length > 0),
    );

    useEffectOnce(() => () => collection.onReset());

    useMeta({ title: 'Управление лентой • Яндекс Ритм' });

    if (!currentUser) {
        return null;
    }

    if (!businessId || !hasAccessToFeedManagement(businessId, currentUser)) {
        return <Navigate to="/" replace />;
    }

    useEffect(() => {
        ymTrackFeedManager({
            page: 'Управления постами',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, []);

    const makeChangeHandler = (name: string) => (value: string | null) => {
        setQuery((searchParams) => {
            ymTrackFeedManagerChangeFiltersParams(name, {
                page: 'Управления постами',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            if (value) {
                searchParams.set(name, value);
            } else {
                searchParams.delete(name);
            }

            collection.onUpdateQuery(makeFilters(searchParams));
            setSelected({});

            return searchParams;
        });
    };

    return (
        <>
            <Flex
                direction="row"
                width="100%"
                px={3}
                alignItems="center"
                spacing={{ xs: 1.5, sm: 2 }}
                borderRadius={BORDER_RADIUS}
                mb={2}
                zIndex={2}
            >
                <Dropdown
                    value={query.get(QUERY_PARAMETER_SOURCE)}
                    placeholder="Источник"
                    options={SOURCE_OPTIONS}
                    onChange={makeChangeHandler('source')}
                />
                <Dropdown
                    value={query.get(QUERY_PARAMETER_STATUS)}
                    placeholder="Статус"
                    options={STATUSES_OPTIONS}
                    onChange={makeChangeHandler('status')}
                />
                <WithHint
                    message={
                        'Здесь находятся все посты и черновики вашего бизнеса.\n\nзелёный – статус опубликован,\nсиний – на модерации,\nкрасный – отклонён модерацией,\nоранжевый – частично отклонён,\nсерый – черновик'
                    }
                />
            </Flex>
            {collection.isLoading || collection.items.length > 0 ? (
                <Box
                    width="100%"
                    background="var(--background-primary)"
                    borderRadius={BORDER_RADIUS}
                    overflow="hidden"
                    pb={identifiers.length > 0 ? 7 : 0}
                >
                    <FeedManagementHeader
                        selected={isAllSelected(collection.items, identifiers)}
                        onSelect={onSelectAll}
                        position="sticky"
                        top={0}
                        background="var(--background-primary)"
                    />
                    <InfiniteList
                        perPage={3}
                        count={collection.items.length}
                        isLoading={collection.isLoading}
                        isEndReached={collection.isEndReached}
                        rowHeight={() => 80}
                        estimatedRowSize={() => 80}
                        onLoadNext={collection.onLoadNext}
                        renderItem={(index) => {
                            const model = collection.items[index];

                            if (model) {
                                return (
                                    <FeedManagementRow
                                        selected={selected[model.id]}
                                        post={toPostUI(model)}
                                        renderMenu={(trigger) => (
                                            <EnhancedPostContextMenu
                                                index={index}
                                                trigger={trigger}
                                                post={model}
                                                user={currentUser}
                                            />
                                        )}
                                        onSelect={(value) => onSelect(model.id, value)}
                                    />
                                );
                            }

                            return <FeedManagementRowSkeleton />;
                        }}
                    />
                </Box>
            ) : (
                <Typography variant="body" weight="medium" color="secondary" m="auto">
                    Ничего не найдено 👀
                </Typography>
            )}
            <StickyFooter
                style={{
                    transform:
                        identifiers.length > 0 ? 'translateY(calc(-100% + 1px))' : 'translateY(0)',
                }}
            >
                {canChangeStatusBulk(
                    collection.items,
                    identifiers,
                    PostStatus.MODERATION,
                    currentUser,
                ) && (
                    <Button
                        color="white"
                        flexGrow={1}
                        onClick={() => onBulkChangeStatus(PostStatus.MODERATION)}
                    >
                        Опубликовать ({identifiers.length})
                    </Button>
                )}
                {canChangeStatusBulk(
                    collection.items,
                    identifiers,
                    PostStatus.DELETED,
                    currentUser,
                ) && (
                    <Button
                        color="white"
                        flexGrow={1}
                        onClick={() => onBulkChangeStatus(PostStatus.DELETED)}
                    >
                        Удалить ({identifiers.length})
                    </Button>
                )}
            </StickyFooter>

            {unloadGuard.state === 'blocked' && (
                <Alert
                    title="Переход"
                    description="Все выбранные посты будут сброшены. Продолжить?"
                    actions={[
                        {
                            label: 'Перейти',
                            onClick: () => unloadGuard.proceed?.(),
                        },
                        {
                            label: 'Остаться',
                            variant: 'filled',
                            onClick: () => unloadGuard.reset?.(),
                        },
                    ]}
                />
            )}
        </>
    );
});
